import { useCurrentOrg, useSessionTokenGetter } from '@develio/auth/client';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

type AuditEntry = {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  changes: Record<string, unknown> | null;
  ip: string | null;
  createdAt: string;
  userId: string | null;
  userEmail: string | null;
  userName: string | null;
};

const ACTION_COLOR: Record<string, string> = {
  create: 'bg-green-50 text-green-700',
  calculate: 'bg-blue-50 text-blue-700',
  update: 'bg-yellow-50 text-yellow-700',
  delete: 'bg-red-50 text-red-600',
};

const ENTITY_LABEL: Record<string, string> = {
  analysis: 'Analysis',
  rims_dataset: 'RIMS Dataset',
  jurisdiction_profile: 'Jurisdiction',
};

export function SettingsAuditRoute() {
  const getToken = useSessionTokenGetter();
  const org = useCurrentOrg();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchEntries = useCallback(
    async (cursor?: string) => {
      const token = await getToken();
      const url = cursor ? `${API}/api/v1/audit-logs?before=${cursor}` : `${API}/api/v1/audit-logs`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error((j as { error?: string }).error ?? `${res.status}`);
      }
      return res.json() as Promise<{ entries: AuditEntry[]; nextCursor: string | null }>;
    },
    [getToken],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchEntries();
        if (!cancelled) {
          setEntries(data.entries);
          setNextCursor(data.nextCursor);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchEntries]);

  async function loadMore() {
    if (!nextCursor) return;
    setLoadingMore(true);
    try {
      const data = await fetchEntries(nextCursor);
      setEntries((prev) => [...prev, ...data.entries]);
      setNextCursor(data.nextCursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingMore(false);
    }
  }

  // Non-owners see a gate
  if (org && org.role !== 'owner') {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-semibold tracking-tight">Audit Log</h2>
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
          <p className="text-sm text-slate-500">Audit log is restricted to Owner role.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Audit Log</h2>
        <p className="mt-1 text-sm text-slate-500">
          All create, update, delete, and calculate events for your organization.
        </p>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600">
          {error}
        </div>
      )}

      {loading && (
        <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
          <p className="text-sm text-slate-500">Loading…</p>
        </div>
      )}

      {!loading && entries.length === 0 && !error && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
          <p className="text-sm text-slate-500">No audit log entries yet.</p>
          <p className="mt-1 text-xs text-slate-400">
            Entries are created when analyses, datasets, or profiles are modified.
          </p>
        </div>
      )}

      {!loading && entries.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-500">When</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Action</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Entity</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Actor</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">IP</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Changes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entries.map((e) => (
                <tr key={e.id} className="hover:bg-slate-50">
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500">
                    {new Date(e.createdAt).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${ACTION_COLOR[e.action] ?? 'bg-slate-100 text-slate-600'}`}
                    >
                      {e.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    <span className="font-medium">
                      {ENTITY_LABEL[e.entityType] ?? e.entityType}
                    </span>
                    {e.entityType === 'analysis' && (
                      <Link
                        to={`/analyses/${e.entityId}`}
                        className="ml-1 text-xs text-slate-400 hover:underline"
                      >
                        ↗
                      </Link>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {e.userName ?? e.userEmail ?? e.userId ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-slate-400">{e.ip ?? '—'}</td>
                  <td className="max-w-xs truncate px-4 py-3 text-xs text-slate-400">
                    {e.changes ? JSON.stringify(e.changes) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {nextCursor && (
            <div className="border-t border-slate-100 px-4 py-3 text-center">
              <button
                type="button"
                onClick={() => void loadMore()}
                disabled={loadingMore}
                className="text-sm text-slate-500 hover:text-slate-900 disabled:opacity-50"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
